import { createFileRoute, Link } from "@tanstack/react-router";
import { AppHeader } from "@/components/AppHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — RoleBoard" },
      {
        name: "description",
        content:
          "The agreement that governs your use of RoleBoard, including ownership of your content and acceptable use.",
      },
      { property: "og:title", content: "Terms of Service — RoleBoard" },
      {
        property: "og:description",
        content:
          "Read the terms that govern your use of RoleBoard.",
      },
      { property: "og:url", content: "https://roleboard.app/terms" },
    ],
    links: [{ rel: "canonical", href: "https://roleboard.app/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <AppHeader />
      <main className="flex-1">
        <article className="container max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Terms of Service
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Last updated: May 2026
            </p>
          </header>

          <Section title="What RoleBoard is">
            <p>
              RoleBoard is an organizational workflow platform for actors. It
              helps you track auditions, organize self tapes, and visualize your
              career momentum. <strong>It is not a predictive casting or
              performance evaluation system.</strong>
            </p>
          </Section>

          <Section title="Your content">
            <p>
              You retain full ownership of everything you upload — tapes,
              notes, audition records. By uploading content, you grant
              RoleBoard a limited license to store, transmit, and display it
              back to you in order to operate the service. We do not claim any
              other rights to your work.
            </p>
          </Section>

          <Section title="Confidentiality">
            <p>
              Audition information often contains confidential project details.
              You are responsible for honoring any confidentiality obligations
              you've agreed to with casting offices, productions, or
              representatives. RoleBoard provides private storage but cannot
              guarantee your compliance with third-party agreements.
            </p>
          </Section>

          <Section title="Acceptable use">
            <p>You agree not to:</p>
            <ul>
              <li>Upload content you do not have the right to store.</li>
              <li>Attempt to access another user's account or data.</li>
              <li>Use the service to harass, defame, or harm others.</li>
              <li>Reverse-engineer, scrape, or abuse the platform.</li>
            </ul>
          </Section>

          <Section title="AI features disclaimer">
            <p>
              AI-assisted features (slate transcription, pattern summaries,
              etc.) are supportive tools and may contain errors. They are not
              professional advice and should not be relied upon as predictions
              of casting outcomes. You are responsible for verifying anything
              generated.
            </p>
          </Section>

          <Section title="Account & termination">
            <p>
              You can delete your account at any time from{" "}
              <Link to="/settings" className="underline">Settings</Link>.
              Deletion permanently removes your auditions, uploaded files, and
              calendar sync data. We may suspend or terminate accounts that
              violate these terms.
            </p>
          </Section>

          <Section title="Limitation of liability">
            <p>
              RoleBoard is provided "as is" without warranties of any kind. To
              the maximum extent permitted by law, RoleBoard and its operators
              are not liable for any indirect, incidental, or consequential
              damages arising from your use of the service.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update these terms as the product evolves. Material changes
              will be communicated through the app or by email.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions? Email{" "}
              <a href="mailto:hello@roleboard.app" className="underline">
                hello@roleboard.app
              </a>
              .
            </p>
          </Section>
        </article>
      </main>
      <SiteFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      <div className="mt-3 text-[15px] leading-relaxed text-muted-foreground space-y-3">
        {children}
      </div>
    </section>
  );
}
